import { useState } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { PlayCircle } from 'react-feather';
import { segmentationBuilderApi } from './segmentation-api';

interface SegmentationFeaturesProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationFeatures;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #e5e5e5;
  background: #fafafa;
`;

const CardBody = styled.div`
  padding: 18px;
`;

const CategoryGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const CategoryCard = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e5e5e5;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CategoryName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;

const CategoryWeight = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #28aae1;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e5e5;
`;

const SliderLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #626262;
  min-width: 50px;
`;

const SliderInput = styled.input`
  flex: 1;
  height: 4px;
  appearance: none;
  background: #e5e5e5;
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #28aae1;
    border: 2px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition: transform 0.15s;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #28aae1;
    border: 2px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    cursor: pointer;
  }
`;

const SliderValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #28aae1;
  min-width: 36px;
  text-align: right;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const FeatureChip = styled.div`
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e5f5fc;
  color: #1d94c7;
  border: 1px solid #b3d9ff;
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #28aae1;
  color: #ffffff;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background: #1d94c7;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e5e5e5;
    color: #adadad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export function SegmentationFeatures({
  queryId,
  queryIdentifier,
  value,
}: SegmentationFeaturesProps) {
  const [processing, setProcessing] = useState(false);
  const [weights, setWeights] = useState<Record<string, number>>(() => {
    if (!value.featureSelection) return {};
    return Object.fromEntries(
      Object.entries(value.featureSelection.categories).map(([name, cat]) => [name, cat.weight])
    );
  });

  const handleWeightChange = (category: string, newWeight: number) => {
    setWeights((prev) => ({ ...prev, [category]: newWeight }));
  };

  const handleContinue = async (): Promise<void> => {
    setProcessing(true);
    try {
      await segmentationBuilderApi.continueFromFeatures(queryIdentifier, weights);
    } finally {
      setProcessing(false);
    }
  };

  const featureSelection = value.featureSelection;

  if (!featureSelection) {
    return (
      <Wrapper>
        <Card>
          <CardBody>
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#adadad' }}>
              Loading feature selection...
            </div>
          </CardBody>
        </Card>
      </Wrapper>
    );
  }

  const categories = Object.entries(featureSelection.categories);

  return (
    <Wrapper>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#323232' }}>Selected Features</div>
          <div style={{ fontSize: 12, color: '#626262', marginTop: 4 }}>
            {categories.reduce((sum, [_, cat]) => sum + cat.features.length, 0)} features across {categories.length}{' '}
            categories
          </div>
        </CardHeader>
        <CardBody>
          <CategoryGrid>
            {categories.map(([categoryName, category]) => (
              <CategoryCard key={categoryName}>
                <CategoryHeader>
                  <CategoryName>{categoryName.replace(/_/g, ' ').toUpperCase()}</CategoryName>
                  <CategoryWeight>{Math.round((weights[categoryName] ?? category.weight) * 100)}%</CategoryWeight>
                </CategoryHeader>
                <FeatureList>
                  {category.features.map((feature) => (
                    <FeatureChip key={feature}>{feature.replace(/_/g, ' ')}</FeatureChip>
                  ))}
                </FeatureList>
                <SliderRow>
                  <SliderLabel>Weight</SliderLabel>
                  <SliderInput
                    type="range"
                    min="0"
                    max="100"
                    value={Math.round((weights[categoryName] ?? category.weight) * 100)}
                    onChange={(e) => handleWeightChange(categoryName, Number(e.target.value) / 100)}
                  />
                  <SliderValue>{Math.round((weights[categoryName] ?? category.weight) * 100)}%</SliderValue>
                </SliderRow>
              </CategoryCard>
            ))}
          </CategoryGrid>
        </CardBody>
      </Card>

      <ContinueButton type="button" disabled={processing} onClick={handleContinue}>
        <PlayCircle size={18} />
        {processing ? 'Starting Segmentation...' : 'Run Segmentation'}
      </ContinueButton>
    </Wrapper>
  );
}
